<template>
    <div>
        <div v-if="loading">
            <loading></loading>
        </div>

        <div v-else>
            <b-alert variant="danger" :show="hasErrors">
                <span v-html="errorMessage"></span>
            </b-alert>
            <h3>{{ pageTitle }}</h3>
            <b-table
                responsive
                small
                :items="filteredItems"
                :fields="fields"
                :filter="filter"
                :filter-included-fields="['search']"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                primary-key="client_id"
                @filtered="onFiltered"
            >
                <template #cell(id)="data">
                    <div class="dropdown">
                        <button
                            id="dropdownMenuButton"
                            class="btn p-0 dropdown-toggle"
                            type="button"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                        >
                            <i class="far fa-bars"></i>
                        </button>
                        <div
                            class="dropdown-menu"
                            aria-labelledby="dropdownMenuButton"
                        >
                            <b-link
                                v-if="showByCompany"
                                class="dropdown-item"
                                @click="
                                    byCompany(data.item.id, data.item.title)
                                "
                            >
                                <i class="fas fa-caret-right fa-fw"></i>
                                {{ data.item.title }} par cabinet
                            </b-link>
                            <b-link
                                v-if="showByActivity"
                                class="dropdown-item"
                                @click="
                                    byActivity(data.item.id, data.item.title)
                                "
                            >
                                <i class="fas fa-caret-right fa-fw"></i>
                                {{ data.item.title }} par activité
                            </b-link>
                            <b-link
                                v-if="showByClient"
                                class="dropdown-item"
                                @click="byClient(data.item.id, data.item.title)"
                            >
                                <i class="fas fa-caret-right fa-fw"></i>
                                {{ data.item.title }} par client
                            </b-link>
                        </div>
                    </div>
                </template>
                <template #cell(current_month)="data">
                    <span>{{ formatNumber(data.item.current_month) }}</span>
                </template>
                <template #cell(past_month)="data">
                    <span>{{ formatNumber(data.item.past_month) }}</span>
                </template>
                <template #cell(month_difference)="data">
                    <span>{{ formatNumber(data.item.month_difference) }}</span>
                </template>
                <template #cell(current_year)="data">
                    <span>{{ formatNumber(data.item.current_year) }}</span>
                </template>
                <template #cell(past_year)="data">
                    <span>{{ formatNumber(data.item.past_year) }}</span>
                </template>
                <template #cell(year_difference)="data">
                    <span>{{ formatNumber(data.item.year_difference) }}</span>
                </template>

                <!-- FOOT -->
                <template #custom-foot="data">
                    <b-tr class="bg-light">
                        <b-th></b-th>
                        <b-th>TOTAL</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.current_month)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.past_month)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.month_difference)
                        }}</b-th>
                        <b-th class="text-right my-vertical-border">{{
                            total_month_evolution
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.current_year)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.past_year)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.year_difference)
                        }}</b-th>
                        <b-th class="text-right">{{
                            total_year_evolution
                        }}</b-th>
                    </b-tr>
                </template>
            </b-table>
            <!-- Pagination -->
            <b-row>
                <b-col md="3" class="my-1 text-center">
                    <b-pagination
                        v-if="totalRows > 0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        class="my-0"
                        size="sm"
                    ></b-pagination>
                </b-col>
                <b-col md="6" class="my-2">
                    <p class="text-center small pt-0">{{ legend }}</p>
                </b-col>
                <b-col md="3" class="my-1 text-right">
                    <b-dropdown
                        right
                        split
                        size="sm"
                        :text="perPage + ' éléments par page'"
                    >
                        <b-dropdown-item
                            v-for="pageOption in pageOptions"
                            :key="pageOption"
                            @click="setPerPage(pageOption)"
                        >
                            {{ pageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>
            </b-row>
            <!-- /Pagination -->
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import loading from '../../../common/components/loading'
import TableHandler from '../../../common/mixins/TableHandler'
import DashboardHandler from '../../mixins/DashboardHandler'
import monthChargeHandler from '../../../common/components/MonthChargeHandler.vue'

export default {
    name: 'EvolutionInteractive',

    components: {
        loading,
    },

    mixins: [TableHandler, DashboardHandler],

    data() {
        return {
            fields: [
                { key: 'id', label: '', class: 'td-icon', sortable: false },
                { key: 'title', label: '', sortable: true },
                {
                    key: 'current_month',
                    label: 'Mois',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'past_month',
                    label: 'Mois (N-1)',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'month_difference',
                    label: 'Ecart',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'month_evolution',
                    label: 'Evolution',
                    class: 'text-right td-dashboard-data my-vertical-border',
                    sortable: true,
                },
                {
                    key: 'current_year',
                    label: 'Cumul',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'past_year',
                    label: 'Cumul (N-1)',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'year_difference',
                    label: 'Ecart',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'year_evolution',
                    label: 'Evolution',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
            ],
            total: {},
            dataUrl: '',
            showByClient: true,
            showByActivity: true,
            showByCompany: true,
            filterType: 'all',
            filterId: '',
            pageTitle: 'Global',
            cellClasses: [],
            total_month_evolution: '?',
            total_year_evolution: '?',
        }
    },

    async created() {
        this.dataUrl = 'dashboard/evolution/global/'
        await this.getData()
    },

    methods: {
        async byCompany(id, title) {
            this.showByClient = true
            this.showByActivity = true
            this.showByCompany = false
            this.dataUrl = 'dashboard/evolution/by-company/'
            this.filterId = id
            await this.getData()
            this.pageTitle = title
            this.filterType = 'company'
        },

        async byActivity(id, title) {
            this.showByClient = true
            this.showByActivity = false
            this.showByCompany = true
            this.dataUrl = 'dashboard/evolution/by-activity/'
            this.filterId = id
            await this.getData()
            this.pageTitle = title
            this.filterType = 'activity'
        },

        async byClient(id, title) {
            this.showByClient = false
            this.showByActivity = true
            this.showByCompany = false
            this.filterId = id
            this.dataUrl = 'dashboard/evolution/by-client/'
            await this.getData()
            this.pageTitle = title
            this.filterType = 'client'
        },

        async getData() {
            this.loading = true
            try {
                const response = await axios.get(
                    `${this.dataUrl + this.year}/${this.month}/${
                        this.filterType
                    }/${this.filterId}`
                )
                this.items = response.data.data
                this.calculateTotal()
                this.updateTotalRows()
                this.loading = false
            } catch (error) {
                console.log(error)
                this.handleErrors(error.response)
            }
        },

        initTotal() {
            this.total.current_month = 0
            this.total.past_month = 0
            this.total.month_difference = 0
            this.total.current_year = 0
            this.total.past_year = 0
            this.total.year_difference = 0
        },

        calculateTotal() {
            this.initTotal()

            this.items.forEach(
                (item) => (this.total.current_month += item.current_month)
            )
            this.items.forEach(
                (item) => (this.total.past_month += item.past_month)
            )
            this.items.forEach(
                (item) => (this.total.month_difference += item.month_difference)
            )
            this.items.forEach(
                (item) => (this.total.current_year += item.current_year)
            )
            this.items.forEach(
                (item) => (this.total.past_year += item.past_year)
            )
            this.items.forEach(
                (item) => (this.total.year_difference += item.year_difference)
            )

            if (this.total.past_month === 0) {
                this.total_month_evolution = '-'
            } else {
                const evolution =
                    (this.total.current_month - this.total.past_month) /
                    this.total.past_month
                this.total_month_evolution = `${Math.floor(
                    100 * evolution
                ).toLocaleString()}%`
            }

            if (this.total.past_year === 0) {
                this.total_year_evolution = '-'
            } else {
                const evolution =
                    (this.total.current_year - this.total.past_year) /
                    this.total.past_year
                this.total_year_evolution = `${Math.floor(
                    100 * evolution
                ).toLocaleString()}%`
            }
        },

        formatNumber(number) {
            if (number === 0) {
                return '-'
            }
            if (number === null) {
                return '-'
            }
            return Math.floor(number).toLocaleString()
        },
    },

    computed: {
        ...mapState({
            year: (state) => state.browsing.year,
            month: (state) => state.browsing.month,
        }),
    },
}
</script>

<style scoped></style>
